import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  getMalnutritionList,
  getMalnutritionRiskAssessment,
  getPatientWeights,
  postMalnutritionFeedback,
} from "./endpoints";
import { malnutritionUseMocks } from "./mocks";

import type { UseMutationResult, UseQueryResult } from "react-query";
import type {
  MalnutritionFeedbackVariables,
  MalnutritionRiskAssessment,
  PatientWeightHistory,
} from "../../types/Malnutrition";
import type MalnutritionListEntry from "../../types/MalnutritionListEntry";

// Both endpoints serve EBA-pushed data that changes with no user action to
// invalidate on, so poll while the embed is open (foreground only —
// refetchIntervalInBackground stays off globally).
const MALNUTRITION_REFETCH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

// The list's drawer remounts the card on every open, and the app-wide
// defaults would otherwise show what was cached when it last closed, missing
// a feedback save that landed after the user moved on.
const CARD_QUERY_OPTIONS = {
  refetchInterval: MALNUTRITION_REFETCH_INTERVAL_MS,
  refetchOnMount: "always" as const,
};

export const useMalnutritionRiskQuery = (
  emrPatientID: string,
  emrHospitalID: string
): UseQueryResult<MalnutritionRiskAssessment, Error> => {
  // The fetcher returns mock vs real data based on the ?mock= param, so the mode
  // is part of the cache identity — otherwise toggling ?mock= without a full
  // reload could serve cached mock data in real mode (or vice versa).
  const useMocks = malnutritionUseMocks();
  return useQuery<MalnutritionRiskAssessment, Error>(
    ["malnutritionrisk", emrPatientID, emrHospitalID, useMocks],
    () => getMalnutritionRiskAssessment(emrPatientID, emrHospitalID),
    { ...CARD_QUERY_OPTIONS, enabled: !!emrPatientID && !!emrHospitalID }
  );
};

// Idle until a board is picked. Stale at once, like the QList's list, so going
// back to a board refetches it instead of showing the roster cached when it
// was left.
export const useMalnutritionListQuery = (
  department?: string,
  group?: string,
  qvHospitalID?: number
): UseQueryResult<MalnutritionListEntry[], Error> => {
  const useMocks = malnutritionUseMocks();
  return useQuery<MalnutritionListEntry[], Error>(
    ["malnutritionlist", department, group, qvHospitalID, useMocks],
    () => getMalnutritionList(department, group, qvHospitalID),
    {
      enabled: !!department || !!group,
      refetchInterval: MALNUTRITION_REFETCH_INTERVAL_MS,
      staleTime: 0,
      refetchOnMount: true,
    }
  );
};

export const usePatientWeightsQuery = (
  emrPatientID: string,
  emrHospitalID: string
): UseQueryResult<PatientWeightHistory, Error> => {
  const useMocks = malnutritionUseMocks();
  return useQuery<PatientWeightHistory, Error>(
    ["patientweights", emrPatientID, emrHospitalID, useMocks],
    () => getPatientWeights(emrPatientID, emrHospitalID),
    { ...CARD_QUERY_OPTIONS, enabled: !!emrPatientID && !!emrHospitalID }
  );
};

// Records one step of the answer — the Yes/No choice or the follow-up. On
// success the risk-assessment query is invalidated so the card re-reads what
// was stored; the key prefix match ignores the trailing useMocks segment. The
// save settles only once that re-read lands, so a panel reopened straight
// after it seeds from the saved answer rather than the one before, and the
// card's pending vote holds until the server's answer replaces it.
//
// The EMR ids are mutation *variables* rather than hook arguments: the panel
// snapshots them when it opens, so a write in flight stays attributed to the
// patient it was dispatched for even if the user switches patients first.
export const useMalnutritionFeedbackMutation = (): UseMutationResult<
  void,
  Error,
  MalnutritionFeedbackVariables
> => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, MalnutritionFeedbackVariables>(
    ({ emrPatientID, emrHospitalID, payload }) =>
      postMalnutritionFeedback(emrPatientID, emrHospitalID, payload),
    {
      onSuccess: (_data, { emrPatientID, emrHospitalID }) =>
        queryClient.invalidateQueries([
          "malnutritionrisk",
          emrPatientID,
          emrHospitalID,
        ]),
    }
  );
};
